import { useEffect, useRef, useState } from 'react'
import type { IndexDef } from '@/api/types/readiness'
import { Icon, Chip, Flag } from '@/components/readiness/primitives'
import { LockedItem } from '@/components/readiness/LockedItem'
import { useIsLocked } from '@/stores/profileStore'

// Header index selector (I1 §5, I2 §12): flag · {regulator} {index} · {jurisdiction}
// · status chip · chevron. Rows carry the scale hint (L0–L3, 0–10) and any
// status chip; disabled (coming soon) entries announce it to screen readers.
// Flags are inline SVG from the index definition, never emoji. In POC (I6 POC
// §3.2) the menu still opens, but every index other than the current one (CSA
// QRI) is a locked row.

const scaleHint = (ix: IndexDef) => (ix.scale === 'score_0_10' ? '0–10' : ix.scale === 'stages' ? 'Stages' : 'L0–L3')

export function IndexSelector({ indexes, current, onSelect }: { indexes: IndexDef[]; current: IndexDef; onSelect: (id: string) => void }) {
  const [open, setOpen] = useState(false)
  const switchLocked = useIsLocked('index_switch')
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false)
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])
  const statusChip = (ix: IndexDef) =>
    ix.status === 'preliminary' ? <Chip tone="warn" style={{ fontWeight: 700, letterSpacing: '0.06em' }}>PRELIMINARY</Chip> : ix.status === 'coming_soon' ? <Chip tone="neutral">Coming soon</Chip> : null
  return (
    <div className="rd relative" ref={ref}>
      <button
        type="button"
        className="rd-btn rd-btn-sm"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Readiness index: ${current.regulator} ${current.name.replace(`${current.regulator} `, '')} · ${current.jurisdiction}${current.status === 'preliminary' ? ' · preliminary' : ''}`}
        onClick={() => setOpen((v) => !v)}
        data-testid="index-selector"
      >
        <Flag svg={current.flag} />
        <span className="hidden sm:inline">{current.name} · {current.jurisdiction}</span>
        <span className="sm:hidden">{current.name}</span>
        {statusChip(current)}
        <Icon name="chevron" size={14} />
      </button>
      {open && (
        <ul role="listbox" aria-label="Readiness index" className="rd-card absolute right-0 top-10 z-50 w-[22rem] max-w-[calc(100vw-16px)] p-1.5" style={{ boxShadow: 'var(--rd-shadow-float)', border: '1px solid var(--rd-border-control)' }}>
          {indexes.map((ix) => {
            const disabled = ix.status === 'coming_soon'
            const selected = ix.id === current.id
            if (switchLocked && !selected) {
              return (
                <li key={ix.id} role="option" aria-selected={false} aria-disabled="true">
                  <LockedItem
                    feature="index_switch"
                    variant="row"
                    className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[14px]"
                    chip={statusChip(ix)}
                    testId={`index-row-locked-${ix.id}`}
                  >
                    <Flag svg={ix.flag} />
                    <span className="min-w-0 flex-1">
                      <span className="font-semibold">{ix.name}</span>
                      <span style={{ color: 'var(--rd-text-muted)' }}> · {ix.jurisdiction}{ix.version ? ` · ${ix.version}` : ix.published_at ? ` · ${ix.published_at}` : ''}</span>
                      <span className="sr-only"> · locked, available in the full version</span>
                    </span>
                    <span className="rd-mono shrink-0 text-[11.5px]" style={{ color: 'var(--rd-text-faint)' }}>{scaleHint(ix)}</span>
                  </LockedItem>
                </li>
              )
            }
            return (
              <li key={ix.id} role="option" aria-selected={selected} aria-disabled={disabled}>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    onSelect(ix.id)
                    setOpen(false)
                  }}
                  className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[14px] disabled:cursor-not-allowed disabled:opacity-60"
                  style={{ background: selected ? 'var(--rd-bg-tile)' : undefined }}
                >
                  <Flag svg={ix.flag} />
                  <span className="min-w-0 flex-1">
                    <span className="font-semibold">{ix.name}</span>
                    <span style={{ color: 'var(--rd-text-muted)' }}> · {ix.jurisdiction}{ix.version ? ` · ${ix.version}` : ix.published_at ? ` · ${ix.published_at}` : ''}</span>
                    {disabled && <span className="sr-only"> coming soon</span>}
                    {ix.status === 'preliminary' && <span className="sr-only"> preliminary</span>}
                  </span>
                  <span className="rd-mono shrink-0 text-[11.5px]" style={{ color: 'var(--rd-text-faint)' }}>{scaleHint(ix)}</span>
                  {statusChip(ix)}
                  {selected && <Icon name="check" size={14} />}
                </button>
              </li>
            )
          })}
          <li className="px-2.5 pb-1 pt-2 text-[11.5px] leading-relaxed" style={{ color: 'var(--rd-text-faint)', borderTop: '1px solid var(--rd-border-subtle)' }} aria-hidden="true">
            Levels, scores and evidence are kept per index. Preliminary indexes are Quantizant's reading of a published report, not the regulator's own scoring.
          </li>
        </ul>
      )}
    </div>
  )
}
